'use client'

import { useEffect, useMemo } from 'react'

import { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { usePLPageMetaOptional } from '@src/components/pl-page-context'
import { STATSIG_GATE_NAMES, useStatsigGate } from '@src/experiments/statsig-integration/use-statsig-gate.hook'
import { setInventoryInSessionStorage } from '@src/quince-site-common/src/services/inventoryService/inventory.sessionStorage'
// The DS-switching wrapper — `shouldUseDesignSystem()` picks the DS or legacy variant per request,
// so PL follows the DS ramp exactly like the non-PL bundle page. Both variants honor
// `purchaseInfoSeed` + `hideInlineSections`; the gallery collapse differs per branch (see below).
import BundleProductPurchase from '@src/quince-site-pdp/src/organisms/productPageSections/bundleProductPurchase/BundleProductPurchase'
import type { TPDPSectionData } from '@src/quince-site-pdp/src/types/pdp-section-data.type'
import { getStoreProgramIdFromStorage } from '@src/utils/b2b/b2b.utils'

import { protoToBundle, protoToMergeProducts } from './bundle-purchase.adapter'
import { getBundleInventorySeed } from './bundle-purchase.inventory'
import styles from './bundle-purchase.module.scss'
import { useBundleSelectionBridge } from './use-bundle-selection-bridge'

import type { BundlePurchaseWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

/**
 * BUNDLE_PURCHASE widget — the PL bundle buy box. The BE emits it instead of PRODUCT_PURCHASE on a
 * bundle; renders `BundleProductPurchase` (the DS/legacy router) off the adapted `Bundle` for full
 * non-PL parity on both branches of the DS ramp.
 */
function BundlePurchaseWidgetComponent(props: PLTypedWidgetProps<BundlePurchaseWidgetData>) {
  const data = props.data.bundlePurchaseData
  const pageMeta = usePLPageMetaOptional()

  // Adapt once per payload; `mergeProducts` stays a sidecar prop, matching non-PL.
  const bundle = useMemo(() => (data ? protoToBundle(data) : undefined), [data])
  const mergeProducts = useMemo(() => (data ? protoToMergeProducts(data) : []), [data])

  // The buy box only reads `selectedOptions` off `sectionData`; empty keeps its own seeding authoritative.
  const sectionData = useMemo(() => ({ selectedOptions: [] }) as unknown as TPDPSectionData, [])

  // Mirror the legacy box's redux selection onto the PL store (reco refetch + bundle totals).
  useBundleSelectionBridge(bundle)

  // Seed inventory from the payload to skip the mount-time per-product fan-out; `undefined` falls back
  // to the legacy fetch. Kill-switch + B2B bail-out mirror the single-product path.
  const forceClientInventory = useStatsigGate(STATSIG_GATE_NAMES.PL_FORCE_CLIENT_INVENTORY)
  const inventorySeed = useMemo(() => {
    if (!data || !bundle || forceClientInventory || getStoreProgramIdFromStorage()) return undefined
    // Only the initially-rendered products — merge siblings are resolved on swap, not seeded.
    return getBundleInventorySeed(data, [...(bundle.customBundleProducts ?? []), ...(bundle.addOnBundleProducts ?? [])])
  }, [data, bundle, forceClientInventory])

  useEffect(() => {
    if (!inventorySeed) return
    setInventoryInSessionStorage({ data: inventorySeed.inventoryMap, code: 200 })
  }, [inventorySeed])

  if (!data || !bundle) return null

  // `styles.host` collapses the LEGACY section's gallery column via `[class*='…']` selectors — PL
  // renders media as its own widget. Those selectors can't match the DS variant's Panda atomic
  // class names, so the DS branch drops its gallery through the typed `hideBundleImages` prop
  // instead; the host wrapper is inert there.
  return (
    <div className={styles.host}>
      <BundleProductPurchase
        bundle={bundle}
        mergeProducts={mergeProducts}
        sectionData={sectionData}
        // Recos are their own PL widgets; the buy box renders none inline.
        recommendedContentfulProductIds={undefined}
        mteId={pageMeta?.pathname}
        purchaseInfoSeed={inventorySeed?.purchaseInfo}
        // The buy box renders ProductDetails + BrandComparison inline on desktop; PL ships both as
        // their own widgets (ACCORDION / PRODUCT_SUMMARY / BRAND_COMPARISON), so the inline copies
        // would duplicate every section. Non-PL leaves this undefined and is unaffected.
        hideInlineSections
        // DS-branch twin of the `styles.host` gallery collapse (PRODUCT_GALLERY owns media in PL).
        hideBundleImages
      />
    </div>
  )
}

export { BundlePurchaseWidgetComponent }
